import { Spin } from "antd";
import { isEqual } from "lodash";
import { Box, Search, SearchIcon } from "lucide-react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { capitalize, formatDimensions } from "~/utils";
import { InventoryStatusCode, InventoryType } from "~/utils/constants";
import { useConsolidateStore } from "./consolidate.store";

type Props = {
  goToNextStep?: () => void;
};

export const SelectContainer: FC<Props> = ({ goToNextStep }) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    selectedContainer,
    setSelectedContainer,
    setListContainers,
    listContainers,
  } = useConsolidateStore();

  const {
    data: containers,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInventoriesInfinite({
    itemType: InventoryType.PACKAGE,
    search: searchTerm,
  });

  const allContainers = useMemo(
    () => containers?.pages.flatMap((page) => page.items) ?? [],
    [containers]
  );

  useEffect(() => {
    if (allContainers.length > 0 && !isEqual(listContainers, allContainers)) {
      setListContainers(allContainers);
    }
  }, [allContainers, listContainers, setListContainers]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleFilterClick = () => {
    setSearchTerm(searchInput);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <h2 className="font-medium">Select a Container</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose a container where the pieces will be stored.
        </p>
      </div>

      <div className="p-4 border-b">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              placeholder="Search containers..."
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchTerm(searchInput);
                }
              }}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={16}
            />
          </div>
          <button
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-1"
            onClick={handleFilterClick}
          >
            <SearchIcon size={16} />
            <span>Search</span>
          </button>
        </div>
      </div>

      <Spin spinning={isLoading && allContainers.length === 0}>
        <div className="divide-y max-h-96 overflow-y-auto">
          {allContainers.length > 0 ? (
            <>
              {allContainers.map((container) => (
                <div
                  key={container.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    selectedContainer?.id === container.id ? "bg-blue-50" : ""
                  }`}
                  onClick={() => {
                    setSelectedContainer(container);
                    if (goToNextStep) goToNextStep();
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <div
                        className={`p-2 rounded-lg ${
                          selectedContainer?.id === container.id
                            ? "bg-blue-100"
                            : "bg-gray-100"
                        }`}
                      >
                        <Box
                          size={20}
                          className={
                            selectedContainer?.id === container.id
                              ? "text-blue-600"
                              : "text-gray-600"
                          }
                        />
                      </div>
                      <div>
                        <div className="font-medium">{container.title}</div>
                        <div className="text-sm text-gray-500">
                          {container.inventoryRef}
                        </div>
                        <div className="flex items-center mt-1 space-x-3">
                          <div className="text-sm">
                            {capitalize(container?.containerType || "")}
                          </div>
                          <div className="text-sm text-gray-500">
                            {formatDimensions(container)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {container.client
                          ? container.client.companyName || "Unnamed Client"
                          : "No client"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {container.location?.name}
                      </div>
                      <div className="text-xs mt-1">
                        <span
                          className={`px-2 py-1 rounded-full ${
                            container.status ===
                            InventoryStatusCode.AVAILABLE_ON_HAND
                              ? "bg-green-100 text-green-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {capitalize(container.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Infinite scroll trigger */}
              <div ref={observerRef} className="h-1"></div>
              {isFetchingNextPage && (
                <div className="p-4 text-center">
                  <Spin />
                </div>
              )}
            </>
          ) : (
            <div className="p-8 text-center">
              <Box size={40} className="mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700">
                {isLoading ? "Loading containers..." : "No containers found"}
              </h3>
              <p className="text-gray-500 mt-1">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "No containers available"}
              </p>
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};
